/**
 * Lead follow-up dates and lead sources for the Enquiries inbox.
 * Pure (no Supabase / React) so it can be unit-tested (tests/follow-ups-lib.spec.ts).
 * The SQL in 20261102005646_lead_follow_ups_and_sources.sql uses the same rules:
 * a follow-up is "due" on or after its date while the lead is still open.
 */
import { addDays, format, parseISO } from 'date-fns';

export const LEAD_SOURCES = [
  { value: 'website', label: 'Website form' },
  { value: 'phone', label: 'Phone' },
  { value: 'referral', label: 'Referral' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'google', label: 'Google' },
  { value: 'other', label: 'Other' },
] as const;

export type LeadSource = (typeof LEAD_SOURCES)[number]['value'];

export const isLeadSource = (value: unknown): value is LeadSource =>
  LEAD_SOURCES.some((s) => s.value === value);

/** Unknown / missing sources read as "website" — that's where untagged leads come from. */
export const leadSourceOf = (value: unknown): LeadSource => (isLeadSource(value) ? value : 'website');

export const leadSourceLabel = (value: unknown): string =>
  LEAD_SOURCES.find((s) => s.value === leadSourceOf(value))?.label ?? 'Website form';

/** Won or lost — these never need chasing. */
export const CLOSED_LEAD_STATUSES = new Set(['converted', 'declined']);

export const isOpenLead = (status: string) => !CLOSED_LEAD_STATUSES.has(status);

export type FollowUpState = 'none' | 'upcoming' | 'due_today' | 'overdue';

const ymd = (d: Date) => format(d, 'yyyy-MM-dd');

/** Where a lead's follow-up stands on `today` (local calendar day). */
export function followUpState(
  lead: { follow_up_on?: string | null; status: string },
  today: Date = new Date(),
): FollowUpState {
  if (!lead.follow_up_on || !isOpenLead(lead.status)) return 'none';
  const t = ymd(today);
  if (lead.follow_up_on === t) return 'due_today';
  return lead.follow_up_on < t ? 'overdue' : 'upcoming';
}

/** Due today or overdue: what the filter, bell and morning email list. */
export const isFollowUpDue = (lead: { follow_up_on?: string | null; status: string }, today: Date = new Date()) => {
  const state = followUpState(lead, today);
  return state === 'due_today' || state === 'overdue';
};

export const countFollowUpsDue = (
  leads: Array<{ follow_up_on?: string | null; status: string }>,
  today: Date = new Date(),
) => leads.filter((l) => isFollowUpDue(l, today)).length;

export const FOLLOW_UP_QUICK_PICKS = [
  { key: 'tomorrow', label: 'Tomorrow', days: 1 },
  { key: '3days', label: 'In 3 days', days: 3 },
  { key: 'next-week', label: 'Next week', days: 7 },
] as const;

export type FollowUpQuickPick = (typeof FOLLOW_UP_QUICK_PICKS)[number]['key'];

export function quickPickDate(key: FollowUpQuickPick, today: Date = new Date()): string {
  const pick = FOLLOW_UP_QUICK_PICKS.find((p) => p.key === key) ?? FOLLOW_UP_QUICK_PICKS[0];
  return ymd(addDays(today, pick.days));
}

/** "Due today" · "Overdue · 2 days" · "Fri 7 Nov" */
export function followUpLabel(lead: { follow_up_on?: string | null; status: string }, today: Date = new Date()): string | null {
  if (!lead.follow_up_on) return null;
  const state = followUpState(lead, today);
  if (state === 'due_today') return 'Due today';
  if (state === 'overdue') {
    const days = Math.round((parseISO(ymd(today)).getTime() - parseISO(lead.follow_up_on).getTime()) / 86_400_000);
    return `Overdue · ${days} ${days === 1 ? 'day' : 'days'}`;
  }
  return format(parseISO(lead.follow_up_on), 'EEE d MMM');
}
